import { authAxios } from "../../utils/session_utils";
import { AxiosRequestConfig } from "axios";
import { FileInfo } from "../types";

/**
 * Fetches a signed URL for a batch job file (file1 or file2) from the auto-podder-uploads bucket
 */
export const fetchBatchJobFileSignedUrl = async (s3Key: string): Promise<string> => {
    const url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/batch-job-files/signed-url?s3Key=${encodeURIComponent(s3Key)}`;
    const config: AxiosRequestConfig = {
        method: "GET",
        url,
        headers: {
            "Content-Type": "application/json",
        },
    };
    const response = await authAxios(config);
    return response.data.url;
};

/**
 * Fetches a signed URL for a program file from the autopodder-programs bucket
 * Prefers programId over s3Key to avoid whitespace issues
 */
export const fetchProgramFileSignedUrl = async (s3Key?: string, programId?: string): Promise<string> => {
    let url: string;
    if (programId) {
        // Prefer programId over s3Key to avoid whitespace issues
        url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/programs/signed-url?programId=${encodeURIComponent(programId)}`;
    } else if (s3Key) {
        // Fallback to s3Key if programId not provided
        url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/programs/signed-url?s3Key=${encodeURIComponent(s3Key)}`;
    } else {
        throw new Error('Either s3Key or programId is required');
    }
    const config: AxiosRequestConfig = {
        method: "GET",
        url,
        headers: {
            "Content-Type": "application/json",
        },
    };
    const response = await authAxios(config);
    return response.data.url;
};

/**
 * Fetches program data by programId
 */
export const fetchProgramById = async (programId: string): Promise<{ s3_key: string; title: string; filename: string }> => {
    const url = `${process.env.REACT_APP_BACK_END_API}/auto-podder/programs/${programId}`;
    const config: AxiosRequestConfig = {
        method: "GET",
        url,
        headers: {
            "Content-Type": "application/json",
        },
    };
    const response = await authAxios(config);
    const program = response.data;
    return {
        s3_key: program.s3_key || program.S3_KEY || '',
        title: program.title || program.TITLE || '',
        filename: program.filename || program.FILENAME || (program.s3_key || program.S3_KEY || '').split('/').pop() || ''
    };
};

/**
 * Extracts filename from S3 key
 */
export const extractFileNameFromS3Key = (s3Key: string): string => {
    if (!s3Key) return '';
    const parts = s3Key.split('/');
    return parts[parts.length - 1] || s3Key;
};

/**
 * Creates FileInfo with just the name (no URL) - used for initial display
 */
export const createFileInfoFromS3Key = (s3Key: string): FileInfo => {
    const fileName = extractFileNameFromS3Key(s3Key);
    return {
        name: fileName,
        url: null,
        loading: false,
        error: null,
        s3Key,
    };
};

/**
 * Fetches presigned URL for file1 on-demand
 */
export const fetchFile1UrlOnDemand = async (fileInfo: FileInfo): Promise<string> => {
    if (!fileInfo.s3Key) {
        throw new Error('S3 key is required to fetch file URL');
    }
    return await fetchBatchJobFileSignedUrl(fileInfo.s3Key);
};

/**
 * Fetches presigned URL for file2 on-demand
 */
export const fetchFile2UrlOnDemand = async (fileInfo: FileInfo): Promise<string> => {
    if (fileInfo.programId) {
        // For program files, prefer programId over s3Key to avoid whitespace issues
        // Pass programId directly to the API instead of s3Key
        return await fetchProgramFileSignedUrl(undefined, fileInfo.programId);
    } else if (fileInfo.s3Key) {
        // Fetch from auto-podder-uploads bucket
        return await fetchBatchJobFileSignedUrl(fileInfo.s3Key);
    } else {
        const error: any = new Error('S3 key or program ID is required to fetch file URL');
        error.response = { status: 400 };
        throw error;
    }
};

/**
 * Fetches file info for file1 (always available, in auto-podder-uploads bucket)
 */
export const fetchFile1Info = async (s3Key: string): Promise<FileInfo> => {
    try {
        const url = await fetchBatchJobFileSignedUrl(s3Key);
        const fileName = extractFileNameFromS3Key(s3Key);
        return {
            name: fileName,
            url,
            loading: false,
            error: null,
        };
    } catch (error: any) {
        const fileName = extractFileNameFromS3Key(s3Key);
        const errorMessage = error.response?.status === 404 
            ? "File not found - This file may have been removed from S3"
            : error.response?.data?.message || error.message || "Unable to fetch file - Please try again later";
        return {
            name: fileName,
            url: null,
            loading: false,
            error: errorMessage,
        };
    }
};

/**
 * Fetches file info for file2
 * If file2 is "none", fetches program file using programId
 */
export const fetchFile2Info = async (file2S3Key: string, programId?: string): Promise<FileInfo> => {
    try {
        // If file2 is "none", fetch program file
        if (file2S3Key === "none" && programId) {
            try {
                const program = await fetchProgramById(programId);
                if (!program.s3_key) {
                    return {
                        name: program.filename || program.title || 'Program File',
                        url: null,
                        loading: false,
                        error: "Program not found - Unable to retrieve structure file",
                    };
                }
                const url = await fetchProgramFileSignedUrl(program.s3_key);
                return {
                    name: program.filename || program.title || extractFileNameFromS3Key(program.s3_key),
                    url,
                    loading: false,
                    error: null,
                };
            } catch (error: any) {
                const errorMessage = error.response?.status === 404
                    ? "Program not found - Unable to retrieve structure file"
                    : error.response?.data?.message || error.message || "Unable to fetch program file - Please try again later";
                return {
                    name: 'Program File',
                    url: null,
                    loading: false,
                    error: errorMessage,
                };
            }
        }
        
        // If file2 is not "none", fetch from auto-podder-uploads bucket
        const url = await fetchBatchJobFileSignedUrl(file2S3Key);
        const fileName = extractFileNameFromS3Key(file2S3Key);
        return {
            name: fileName,
            url,
            loading: false,
            error: null,
        };
    } catch (error: any) {
        const fileName = file2S3Key === "none" ? 'Program File' : extractFileNameFromS3Key(file2S3Key);
        const errorMessage = error.response?.status === 404
            ? "File not found - This file may have been removed from S3"
            : error.response?.data?.message || error.message || "Unable to fetch file - Please try again later";
        return {
            name: fileName,
            url: null,
            loading: false,
            error: errorMessage,
        };
    }
};
